import React from "react";
import { useNavigate } from "react-router";
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { loginWithEmail } from "../../lib/api";

export default function Login() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      await loginWithEmail(email, password);

      navigate("/amministrazione/contabilita", { replace: true });
    } catch (err: any) {
      setError(err?.message || "Errore di autenticazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 overflow-hidden">
      {/* Glow decorativo */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />

      <form
        onSubmit={onSubmit}
        className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-white/10 backdrop-blur-xl shadow-2xl p-8 text-slate-100"
      >
        {/* Header / Logo */}
        <div className="mb-8 flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Italpreziosi"
            className="h-8 w-auto object-contain"
          />
          <div className="flex flex-col">
            <span className="text-lg font-semibold tracking-tight">
              Data Platform
            </span>
            <span className="text-[11px] uppercase tracking-widest text-slate-300/80">
              Accesso riservato
            </span>
          </div>
        </div>

        {/* Alert errore */}
        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <p className="font-medium">Accesso non riuscito</p>
              <p className="text-red-200/90">{error}</p>
            </div>
          </div>
        )}

        {/* Email */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-slate-200/90">
            Email
          </label>
          <div className="relative mt-1">
            <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300/80" />
            <input
              className="w-full rounded-xl border border-white/10 bg-white/5 px-10 py-2.5 text-sm placeholder:text-slate-300/60 outline-none ring-0 transition focus:border-white/20 focus:bg-white/10"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>
        </div>

        {/* Password */}
        <div className="mb-2">
          <label className="block text-xs font-medium text-slate-200/90">
            Password
          </label>
          <div className="relative mt-1">
            <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300/80" />
            <input
              className="w-full rounded-xl border border-white/10 bg-white/5 px-10 py-2.5 pr-11 text-sm placeholder:text-slate-300/60 outline-none transition focus:border-white/20 focus:bg-white/10"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-300/80 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              aria-label={showPassword ? "Nascondi password" : "Mostra password"}
              title={showPassword ? "Nascondi password" : "Mostra password"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Submit */}
        <div className="mb-2 mt-6">
          <button
            type="submit"
            disabled={loading || !email || !password}
            className="group relative flex w-full items-center justify-center gap-2 rounded-xl bg-white text-slate-900 px-4 py-2.5 text-sm font-semibold transition hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 disabled:opacity-60 disabled:hover:bg-white"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Accesso…
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 transition group-hover:rotate-6" />
                Accedi
              </>
            )}
          </button>
        </div>

        {/* Footer mini */}
        {/* <p className="mt-6 text-center text-[11px] text-slate-300/70">
          Accesso protetto. Ogni attività può essere monitorata per motivi di sicurezza.
        </p> */}
      </form>
    </div>
  );
}
